import { InjectionToken } from '@angular/core';

/**
 * Default list of public route path prefixes that do not require authentication.
 * Used as the default value for the `AUTH_PUBLIC_PATHS` injection token.
 */
export const DEFAULT_AUTH_PUBLIC_PATHS: string[] = [
  '/docs',
  '/forgot-password',
  '/login',
  '/redirect',
  '/reset-password',
  '/sign-up',
];

/**
 * Route prefixes where authenticated users are redirected to home — auth
 * acquisition flows only. Excludes `/docs`, which stays reachable when logged in.
 */
export const DEFAULT_AUTH_GUEST_ONLY_PATHS: string[] = [
  '/forgot-password',
  '/login',
  '/redirect',
  '/reset-password',
  '/sign-up',
];

/** Returns whether an authenticated user should be redirected away from `urlPath`. */
export function isAuthGuestOnlyPath(urlPath: string): boolean {
  return DEFAULT_AUTH_GUEST_ONLY_PATHS.some((path) => urlPath.startsWith(path));
}

/**
 * Injection token for the list of route path prefixes that are accessible
 * without authentication (i.e. public pages).
 *
 * `AuthSessionStore` uses this token to decide whether to redirect to `/login`
 * on logout, and to redirect authenticated users away from auth pages.
 *
 * Override this token in a brand's `app.config.ts` to customise which paths
 * are considered public for that brand.
 *
 * @example
 * // In app.config.ts — override with a custom list:
 * { provide: AUTH_PUBLIC_PATHS, useValue: ['/login', '/sso'] }
 * @example
 * // In app.config.ts — extend the defaults:
 * { provide: AUTH_PUBLIC_PATHS, useValue: [...DEFAULT_AUTH_PUBLIC_PATHS, '/sso'] }
 */
export const AUTH_PUBLIC_PATHS = new InjectionToken<string[]>(
  'AUTH_PUBLIC_PATHS',
  { factory: () => DEFAULT_AUTH_PUBLIC_PATHS }
);
